import { MainMenuItem } from 'components';
import CommunityTitle from 'components/CommunityTitle';
import DropdownMenu from 'components/DropdownMenu';
import { ROUTES } from 'configs';
import { COMMUNITY_ROUTES, PROFILE_ROUTES } from 'configs/routes';
import { useInfoPopup } from 'hooks';
import { isEmpty } from 'lodash/fp';
import { noop } from 'lodash/fp';
import React, { useRef } from 'react';
import { TFunction } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { useToasts } from 'react-toast-notifications';
import {
  DefaultAvatar,
  DeleteIcon,
  EditIcon,
  GroupIcon,
  HomePageIcon,
  LeftCloseArrow,
  ThreeDotIcon,
} from 'static';
import { doDeleteChatGroup } from 'store/actionCreators/chat';
import { openModal } from 'store/actionCreators/modal';
import { ChatUser } from 'store/reducers/chat';
import { CommunityUser, ModalTypes } from 'types';
import { getProfileNameCapital } from 'utils/common';

import {
  Button,
  Description,
  ImageIcon,
  ImageWrapper,
  InfoWrapper,
  LabelWrapper,
  Layout,
  MenuItem,
  MenuItemWrapper,
  SvgWrapper,
} from '../styled';

interface HeaderProps {
  isSidebarOpen: boolean;
  openUserList: boolean;
  toggleSidebar: noop;
  selectedPeerUser: ChatUser | null;
  communityId: string;
  member: CommunityUser;
  talkId: string;
  peerId: string;
  isGroup: boolean;
  members: ChatUser[];
  groupName: string;
  description: string;
  logo: string;
  createdAt: string;
  userId: string;
  admin: string[];
  openViewMemberSidebar: (userId: string) => void;
  loading: boolean;
  t: TFunction;
  headerDesc: string;
}

const Header: React.FC<HeaderProps> = ({
  isSidebarOpen,
  openUserList,
  toggleSidebar,
  selectedPeerUser,
  communityId,
  talkId,
  peerId,
  isGroup,
  member,
  members,
  groupName,
  description,
  createdAt,
  logo,
  userId,
  admin,
  openViewMemberSidebar,
  loading,
  headerDesc,
  t,
}) => {
  const { showInfoPopup } = useInfoPopup();
  const dispatch = useDispatch();
  const childRef = useRef<{ closeMenu: () => void }>(null);
  const { addToast } = useToasts();
  const { communityId: communityIdFromParams } = useParams<{
    communityId: string;
  }>();
  const navigate = useNavigate();

  const onAboutModal = () => {
    if (childRef?.current?.closeMenu) childRef.current?.closeMenu();
    dispatch(
      openModal({
        type: ModalTypes.ABOUT_CHAT_GROUP,
        placement: 'right',
        data: {
          chat: {
            id: talkId,
            chatName: groupName,
            description: description,
            logo: logo || null,
            admin: admin,
          },
          createdAt: createdAt,
          members,
          communityId,
        },
      }),
    );
  };

  const onUpdateGroupHandler = () => {
    if (!admin?.includes(userId)) {
      addToast(t('common.onlyAdmin'), {
        appearance: 'info',
        autoDismiss: true,
      });
      return;
    }
    dispatch(
      openModal({
        type: ModalTypes.UPDATE_GROUP_CHAT,
        placement: 'right',
        data: {
          chat: {
            id: talkId,
            chatName: groupName,
            description: description,
            logo: logo || null,
            admin: admin,
          },
          members,
          communityId,
          isChatUpdate: true,
        },
      }),
    );
  };

  const onUpdateGroupMember = () => {
    if (!admin?.includes(userId)) {
      addToast(t('common.onlyAdmin'), {
        appearance: 'info',
        autoDismiss: true,
      });
      return;
    }
    dispatch(
      openModal({
        type: ModalTypes.ADD_GROUP_MEMBERS_CHAT,
        placement: 'right',
        data: {
          communityId,
          isUpdate: true,
          chat: {
            id: talkId,
            chatName: groupName,
            description: description,
            admin: admin,
          },
          members,
        },
      }),
    );
  };

  const onDeleteGroup = () => {
    dispatch(
      doDeleteChatGroup.trigger({
        userId,
        chatId: talkId,
        onSuccess: () => {
          if (childRef?.current?.closeMenu) childRef.current?.closeMenu();
          addToast(t('chat.delete.success'), {
            appearance: 'success',
            autoDismiss: true,
          });
          if (communityIdFromParams) {
            navigate(
              `${ROUTES.COMMUNITY}/${communityIdFromParams}/${COMMUNITY_ROUTES.CHAT}`,
            );
          } else {
            navigate(`${ROUTES.PROFILE}/${PROFILE_ROUTES.CHAT}`);
          }
        },
      }),
    );
  };

  const image = logo || selectedPeerUser?.logo || member?.profile?.logo || '';
  const title = isGroup
    ? groupName
      ? groupName.charAt(0).toUpperCase()
      : ''
    : selectedPeerUser?.firstName && selectedPeerUser?.lastName
    ? selectedPeerUser.firstName.charAt(0).toUpperCase() +
      selectedPeerUser.lastName.charAt(0).toUpperCase()
    : selectedPeerUser?.profileName
    ? selectedPeerUser.profileName.charAt(0).toUpperCase()
    : member?.profile?.firstName && member?.profile?.lastName
    ? member.profile.firstName.charAt(0).toUpperCase() +
      member.profile.lastName.charAt(0).toUpperCase()
    : member?.profile?.profileName
    ? member.profile.profileName.charAt(0).toUpperCase()
    : '';

  const name = isGroup
    ? groupName
    : (selectedPeerUser?.firstName && selectedPeerUser?.lastName
        ? getProfileNameCapital({
            firstName: selectedPeerUser.firstName,
            lastName: selectedPeerUser.lastName,
          })
        : selectedPeerUser?.profileName) ||
      (member?.profile?.firstName && member?.profile?.lastName
        ? getProfileNameCapital({
            firstName: member.profile.firstName,
            lastName: member.profile.lastName,
          })
        : member?.profile?.profileName) ||
      t('chat.popover.title');

  const data = [
    {
      name: t('chat.modal.about'),
      id: 1,
      onClick: onAboutModal,
      icon: <HomePageIcon width={14} height={14} />,
    },
    {
      name: t('chat.modal.editRoom'),
      id: 2,
      icon: <EditIcon width={14} height={14} />,
      onClick: onUpdateGroupHandler,
    },
    {
      name: t('chat.modal.editMember'),
      id: 3,
      icon: <GroupIcon width={14} height={14} />,
      onClick: () => onUpdateGroupMember(),
    },
    {
      name: t('chat.modal.delete'),
      id: 4,
      icon: <DeleteIcon width={14} height={14} />,
      onClick: () => {
        if (!admin?.includes(userId)) {
          addToast(t('common.onlyAdmin'), {
            appearance: 'info',
            autoDismiss: true,
          });
          return;
        }
        showInfoPopup({
          title: t('calendarConnection.disconnectConfirmTitle'),
          message: t('common.removeLogo'),
          type: 'warning',
          buttonText: t('common.delete'),
          cancelButtonText: t('common.cancel'),
          onButtonClick: onDeleteGroup,
        });
      },
    },
  ];

  return (
    <Layout.Header isOpen={isSidebarOpen || openUserList} isGroup={isGroup}>
      {!openUserList && (
        <Layout.Header.Close onClick={toggleSidebar}>
          <LeftCloseArrow width={20} height={20} />
        </Layout.Header.Close>
      )}
      {!(isEmpty(talkId) && isEmpty(peerId)) && (
        <>
          <InfoWrapper
            onClick={() =>
              openViewMemberSidebar(
                selectedPeerUser?.userId || member?.profile?.userId,
              )
            }
          >
            <ImageWrapper>
              {!loading ? (
                title ? (
                  <CommunityTitle
                    image={image}
                    title={title}
                    isMenuOpen={true}
                    showTitle={false}
                  />
                ) : (
                  <MainMenuItem image={DefaultAvatar} />
                )
              ) : (
                <span className="skeleton skeleton-image" />
              )}
            </ImageWrapper>
            <LabelWrapper>
              <Layout.Header.Name>
                {!loading ? name : <div className="skeleton skeleton-text" />}
              </Layout.Header.Name>
              <Description>
                {loading ? (
                  <div className="skeleton skeleton-text" />
                ) : (
                  <span>{headerDesc}</span>
                )}
              </Description>
            </LabelWrapper>
          </InfoWrapper>
          {isGroup && !loading ? (
            <DropdownMenu
              ref={childRef}
              trigger={
                <Button>
                  <SvgWrapper>
                    <ThreeDotIcon width={18} height={18} />
                  </SvgWrapper>
                  <p>{t('chat.modal.btn')}</p>
                </Button>
              }
            >
              <MenuItemWrapper>
                {data.map((data) => {
                  return (
                    <MenuItem key={data.id} onClick={data.onClick}>
                      <ImageIcon>{data.icon}</ImageIcon>
                      <p>{data.name}</p>
                    </MenuItem>
                  );
                })}
              </MenuItemWrapper>
            </DropdownMenu>
          ) : (
            <span />
          )}
        </>
      )}
    </Layout.Header>
  );
};

export default Header;
